<template>
  <div class="reference-structure">
    <p class="reference-structure__description">
      Для выявления различий в ЭИ-профилях между сотрудниками с высокими и низкими уровнями достижения KPI,
      были отобраны 2 контрастные группы. Основанием для выделения контрастных групп по уровню KPI является
      показатель стандартного отклонения общего уровня KPI по всей выборке (Анастази и др., 1982).
    </p>

    <div class="reference-structure__color-note">
      <p>
        <span class="note-bold">Яркий цвет</span> - ключевой параметр.
      </p>

      <p>
        <span class="note-bold">Бледный цвет</span> - неважный параметр.
      </p>
    </div>

    <div class="reference-structure__grid">
      <a-card class="reference-structure__card" :bordered="true">
        <template #title>
          <div class="reference-structure__card-title">
            Средний ЭИ-профиль
          </div>
        </template>

        <ReferenceStructureLegend
            :low-color="lowColor"
            :high-color="highColor"
        />

        <EiScaleRowsTable
            class="reference-structure__scale reference-structure__scale_hide-header-values"
            :sections="averageProfileSections"
            mode="compare"
            :show-track-values="true"
            :show-difference="false"
            :show-scale-after-each-section="true"
            :show-first-section-title="false"
            :default-color="highColor"
            :default-compare-color="lowColor"
            :scale-height="12"
            :compare-scale-height="12"
            :row-gap="12"
        />
      </a-card>

      <a-card class="reference-structure__card" :bordered="true">
        <template #title>
          <div class="reference-structure__card-title">
            Структура эталонного ЭИ-профиля
          </div>
        </template>

        <ReferenceStructureLegend
            :low-color="lowColor"
            :high-color="highColor"
        />

        <EiScaleRowsTable
            class="reference-structure__scale reference-structure__scale_hide-header-values"
            :sections="detailsSections"
            mode="compare"
            :show-track-values="true"
            :show-difference="false"
            :show-scale-after-each-section="false"
            :show-first-section-title="true"
            :default-color="highColor"
            :default-compare-color="lowColor"
            :scale-height="12"
            :compare-scale-height="12"
            :row-gap="12"
        />
      </a-card>
    </div>
  </div>
</template>

<script>
import { computed } from "vue";
import EiScaleRowsTable from "@/components/Analytics/EiScaleRowsTable.vue";
import ReferenceStructureLegend from "./ReferenceStructureLegend.vue";

export default {
  name: "ReferenceStructureBlock",

  components: {
    EiScaleRowsTable,
    ReferenceStructureLegend,
  },

  props: {
    cards: {
      type: Array,
      default: () => [],
    },

    lowColor: {
      type: String,
      default: "#8EA9DB",
    },

    highColor: {
      type: String,
      default: "#2E75B5",
    },
  },

  setup(props) {
    const detailsKeys = [
      "section_1",
      "section_5",
      "section_9a",
      "section_2",
      "section_6",
      "section_3",
      "section_7",
      "section_9b",
      "section_4",
      "section_8",
    ];

    const cardsByKey = computed(() => {
      return props.cards.reduce((result, card) => {
        if (card.key) {
          result[card.key] = card;
        }

        return result;
      }, {});
    });

    const getCard = (key) => {
      return cardsByKey.value[key] || {};
    };

    const toNumber = (value, fallback = 0) => {
      const number = Number(value);

      return Number.isFinite(number) ? number : fallback;
    };

    const buildRow = (key) => {
      const card = getCard(key);

      const isImportant = Boolean(card.important);
      const lowValue = toNumber(card.avg_low_kpi);
      const highValue = toNumber(card.max);

      return {
        key,
        label: card.title || key,
        helpText: card.descriptionContent || "",
        value: highValue,
        compareValue: lowValue,
        difference: highValue - lowValue,
        color: props.highColor,
        compareColor: props.lowColor,
        muted: !isImportant,
      };
    };

    const getRowsByKeys = (keys) => {
      return keys
          .map((key) => buildRow(key))
          .filter((row) => row.value || row.compareValue);
    };

    const averageProfileSections = computed(() => [
      {
        key: "general",
        title: "",
        rows: getRowsByKeys(["total"]),
      },
      {
        key: "domains",
        title: "Домены",
        rows: getRowsByKeys(["domain_1", "domain_2"]),
      },
      {
        key: "branches",
        title: "Ветви",
        rows: getRowsByKeys(["branch_3", "branch_4", "branch_1", "branch_2"]),
      },
    ]);

    const detailsSections = computed(() => [
      {
        key: "sections",
        title: "Разделы",
        rows: getRowsByKeys(detailsKeys),
      },
    ]);

    return {
      averageProfileSections,
      detailsSections,
    };
  },
};
</script>

<style lang="scss" scoped>
.reference-structure__description {
  max-width: 1120px;
  margin: 0 0 20px;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.reference-structure__color-note {
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);

  p {
    margin: 0;
  }

  .note-bold {
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 120%;
    color: rgba(0, 0, 0, 0.88);
  }
}

.reference-structure__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.reference-structure__card {
  width: 100%;
  min-width: 0;
  border-radius: 7px;

  :deep(.ant-card-head) {
    min-height: auto;
    padding: 0 20px;
    border-bottom: none;
  }

  :deep(.ant-card-head-title) {
    padding: 20px 0 0;
  }

  :deep(.ant-card-body) {
    padding: 0 20px 20px;
  }
}

.reference-structure__card-title {
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.85);
}

.reference-structure__scale {
  width: 100%;

  :deep(.ei-scale-table__row-header) {
    margin-bottom: 8px;
  }

  :deep(.ei-scale-table__label) {
    font-size: 14px;
    color: rgba(0, 0, 0, 0.72);
  }

  :deep(.ei-scale-table__section-title) {
    margin: 0 0 12px;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.42);
  }

  :deep(.ei-scale-table__track) {
    background-color: #f1f4f8;
  }

  :deep(.ei-scale-table__scale) {
    margin-top: 8px;
    font-size: 12px;
  }

  :deep(.ei-scale-table__rows-guide) {
    background: rgba(0, 0, 0, 0.06);
  }
}

.reference-structure__scale_hide-header-values {
  :deep(.ei-scale-table__value) {
    display: none;
  }

  :deep(.ei-scale-table__right) {
    display: none;
  }
}

@media (max-width: $tablet-breakpoint) {
  .reference-structure__grid {
    grid-template-columns: 1fr;
  }
}
</style>